import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, PaperPlaneTilt, CheckCircle, XCircle, Clock, Envelope, Warning } from '@phosphor-icons/react';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notifications = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [lastCheckResult, setLastCheckResult] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/notifications/status`),
      axios.get(`${API}/notifications/email-logs`)
    ]).then(([statusRes, logsRes]) => {
      setStatus(statusRes.data);
      setLogs(logsRes.data);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, []);

  const handleCheck = async () => {
    setChecking(true);
    try {
      const resp = await axios.post(`${API}/notifications/check-deliveries`);
      setLastCheckResult(resp.data);
      const logsResp = await axios.get(`${API}/notifications/email-logs`);
      setLogs(logsResp.data);
    } catch (error) {
      notify(error.response?.data?.detail || 'Error checking deliveries');
    } finally { setChecking(false); }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="notifications-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Email Notifications</h1>
          <p className="text-[#6D8E87]">Manage delivery deadline alerts and email logs</p>
        </div>
        <button onClick={handleCheck} disabled={checking}
          className="flex items-center gap-2 px-6 py-3 bg-[#00a69c] text-white rounded-full font-semibold hover:bg-[#00bfb3] transition disabled:opacity-50"
          data-testid="check-deliveries-btn">
          <PaperPlaneTilt size={20} weight="bold" />
          {checking ? 'Checking...' : 'Run Delivery Check'}
        </button>
      </div>

      {/* Status Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="medical-card p-5" data-testid="email-status">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status?.email_enabled ? 'bg-green-100' : 'bg-amber-100'}`}>
              {status?.email_enabled ? <CheckCircle size={22} className="text-green-600" weight="bold" /> : <Warning size={22} className="text-amber-600" weight="bold" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#052A23]">Email Status</p>
              <p className={`text-xs font-bold ${status?.email_enabled ? 'text-green-600' : 'text-amber-600'}`}>
                {status?.email_enabled ? 'LIVE' : 'MOCKED'}
              </p>
            </div>
          </div>
          <p className="text-xs text-[#6D8E87]">{status?.provider}</p>
        </div>

        <div className="medical-card p-5">
          <p className="text-xs text-[#6D8E87]">Total Emails Logged</p>
          <p className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{logs.length}</p>
        </div>

        <div className="medical-card p-5">
          <p className="text-xs text-[#6D8E87]">Sent / Mocked / Failed</p>
          <p className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>
            <span className="text-green-600">{logs.filter(l => l.status === 'sent').length}</span>
            {' / '}
            <span className="text-amber-600">{logs.filter(l => l.status === 'mocked').length}</span>
            {' / '}
            <span className="text-red-500">{logs.filter(l => l.status === 'failed').length}</span>
          </p>
        </div>
      </div>

      {/* Last Check Result */}
      {lastCheckResult && (
        <div className="medical-card p-5 mb-6 border-2 border-[#00a69c]" data-testid="check-result">
          <h3 className="text-sm font-semibold text-[#00a69c] mb-3">Last Check Result</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><p className="text-xs text-[#6D8E87]">Total Alerts</p><p className="font-bold">{lastCheckResult.total_alerts}</p></div>
            <div><p className="text-xs text-[#6D8E87]">Exec Alerts</p><p className="font-bold">{lastCheckResult.exec_alerts_sent}</p></div>
            <div><p className="text-xs text-[#6D8E87]">RSM Digests</p><p className="font-bold">{lastCheckResult.rsm_digests_sent}</p></div>
            <div><p className="text-xs text-[#6D8E87]">Emails Sent</p><p className="font-bold text-green-600">{lastCheckResult.emails_sent}</p></div>
            <div><p className="text-xs text-[#6D8E87]">Emails Mocked</p><p className="font-bold text-amber-600">{lastCheckResult.emails_mocked}</p></div>
          </div>
        </div>
      )}

      {/* Email Logs */}
      <div className="medical-card overflow-hidden" data-testid="email-logs">
        <div className="p-5 border-b border-[#D5E8E4]">
          <h3 className="text-lg font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Email Log</h3>
        </div>
        {logs.length === 0 ? (
          <div className="p-12 text-center">
            <Envelope size={40} className="text-[#D5E8E4] mx-auto mb-3" />
            <p className="text-[#6D8E87]">No emails sent yet. Click "Run Delivery Check" to trigger notifications.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-[#F4F9F7]">
              <tr>
                <th className="text-left px-5 py-3 text-xs uppercase text-[#3B5A54]">Status</th>
                <th className="text-left px-5 py-3 text-xs uppercase text-[#3B5A54]">To</th>
                <th className="text-left px-5 py-3 text-xs uppercase text-[#3B5A54]">Subject</th>
                <th className="text-left px-5 py-3 text-xs uppercase text-[#3B5A54]">Type</th>
                <th className="text-left px-5 py-3 text-xs uppercase text-[#3B5A54]">Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={i} className="border-t border-[#D5E8E4] hover:bg-[#FAFFFE]">
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      log.status === 'sent' ? 'bg-green-100 text-green-700' :
                      log.status === 'mocked' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {log.status === 'sent' ? <CheckCircle size={12} /> : log.status === 'mocked' ? <Clock size={12} /> : <XCircle size={12} />}
                      {log.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#052A23] font-medium">{log.to}</td>
                  <td className="px-5 py-3 text-[#3B5A54] max-w-xs truncate">{log.subject}</td>
                  <td className="px-5 py-3">
                    <span className="text-xs text-[#6D8E87]">{log.meta?.type || '-'}</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#6D8E87]">{new Date(log.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
